import React, { FC } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import Icon from 'components/Icon'

type PaginationProps = {
  currentPage: number
  totalPages: number
  baseUrl: string
}

const Pagination: FC<PaginationProps> = ({ currentPage, totalPages, baseUrl }) => {
  if (totalPages <= 1) return null

  // Generate page numbers to show (with ellipses if many pages)
  const getPages = () => {
    const pages: (number | string)[] = []
    const range = 2 // Number of pages to show around current page

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - range && i <= currentPage + range)
      ) {
        pages.push(i)
      } else if (
        (i === currentPage - range - 1 && i > 1) ||
        (i === currentPage + range + 1 && i < totalPages)
      ) {
        pages.push('...')
      }
    }
    return pages.filter((v, i, a) => v !== '...' || a[i - 1] !== '...')
  }

  const pages = getPages()

  return (
    <nav aria-label='Blog pagination' className='blog-pagination mt-12 mt-md-18'>
      <ul className='pagination justify-content-center flex-wrap list-unstyled d-flex align-items-center gap-2'>
        {currentPage > 1 && (
          <li className='page-item'>
            <Link
              href={currentPage === 2 ? baseUrl : `${baseUrl}?page=${currentPage - 1}`}
              className='page-link'
              aria-label='Previous page'
            >
              <Icon name='chevron-left' width={12} height={12} fill='grey5' />
            </Link>
          </li>
        )}

        {pages.map((p, i) => (
          <li
            key={i}
            className={classNames('page-item', {
              active: p === currentPage,
              disabled: p === '...',
            })}
          >
            {p === '...' ? (
              <span className='page-link border-0'>...</span>
            ) : (
              <Link
                href={p === 1 ? baseUrl : `${baseUrl}?page=${p}`}
                className='page-link'
              >
                {p}
              </Link>
            )}
          </li>
        ))}

        {currentPage < totalPages && (
          <li className='page-item'>
            <Link
              href={`${baseUrl}?page=${currentPage + 1}`}
              className='page-link'
              aria-label='Next page'
            >
              <Icon name='chevron-right' width={12} height={12} fill='grey5' />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  )
}

export default Pagination
